
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { Row, Col, Alert, Form } from 'react-bootstrap';
import Card from '../../../components/Card';

import { SiteItem } from '../../../features/sites';
import { getSites, getPropertyInfo } from '../../../services/api/site';
import { ApiError, Site } from '../../../services/openapi';

import { useKeycloak } from '@react-keycloak/web';

import SearchInput from '../../../components/SearchInput';
import ListPagination from '../../../components/ListPagination';
import RefreshPull from '../../../components/RefreshPull';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { peekPendingSelectedId, clearPendingSelectedId } from '../../../hooks/pendingSelection';
import { useTranslation } from 'react-i18next';

const Sites = () => {
    const [sites, setSites] = useState<Site[]>([]);        
    const [siteCount, setSiteCount] = useState<string>("0");    
    const [pagingString, setPagingString] = useState<string>("0-25");    
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
    const [tableBodyHeight, setTableBodyHeight] = useState<number>(380);
    const [formData, setFormData] = useLocalStorage<string>("sites", "formData", "");
    const [searchData, setSearchData] = useLocalStorage<Record<string, string>>("sites", "searchDataSites", {});
    const [filterData, setFilterData] = useLocalStorage<string>("sites", "filterData", "");
    const [currentPage, setCurrentPage] = useLocalStorage<number>("sites", "currentPage", 0);
    const [siteIdFilter, setSiteIdFilter] = useLocalStorage<string[]>("sites", "siteFilter", []);
    const [siteTypeFilter, setSiteTypeFilter] = useLocalStorage<string[]>("sites", "siteTypeFilter", []);
    const [siteFilterQuery, setSiteFilterQuery] = useLocalStorage<string>("sites", "siteFilterQuery", "");
    const [errorMsg, setErrorMsg] = useState<string[]>([]);
    const [newSite, setNewSite] = useState<Site>();
    const [selectedId, setSelectedId] = useState<number | null>(() => peekPendingSelectedId("sites"));
    const [rowClickCount, setRowClickCount] = useState({});

    const { keycloak, initialized } = useKeycloak();
    const { t } = useTranslation();

    const navigate = useNavigate();

    const setData = (data: Site) => {
        localStorage.setItem('Site', JSON.stringify(data));
    }

    const handleRowClick = (site: Site) => {
        if (window.matchMedia('(pointer: coarse)').matches) {
            setRowClickCount({ [site.id]: 1 });
        }

        setRowClickCount((prev) => {
            const newCount = (prev[site.id] || 0) + 1;
            if (newCount === 1) {
                setSelectedId(site.id);
                return { ...prev, [site.id]: newCount };
            } else if (newCount === 2) {
                setData(site);
                setSelectedId(site.id);
                navigate("/dashboard/details/site");
                return { ...prev, [site.id]: 0 };
            }
            return prev;
        });
    };

    const handleMouseClickOnRow = (e: React.MouseEvent<HTMLElement>, site: Site) => {
        const el = e.target as HTMLInputElement;

        if (el != null && el.type === 'checkbox') {
            return;
        }

        handleRowClick(site);
    }

    const handleFilter = (e: string): void => {
        setSites([]);
        setFilterData(e);
        setCurrentPage(0);
    }

    const handleSelectAll = (e: ChangeEvent<HTMLInputElement>): void => {
        if (e.target.checked) {
            setSiteIdFilter([]);
            setSiteTypeFilter([]);
            setSiteFilterQuery(JSON.stringify(searchData))
        }
        else {
            setSiteFilterQuery("")
        }
    }

    const handleSearch = (e: string): void => {
        setSites([]);
        searchData["search"] = e;
        setSearchData(searchData);

        if (siteFilterQuery.length > 0) {
            setSiteIdFilter([]);
            setSiteTypeFilter([]);
            setSiteFilterQuery(JSON.stringify(searchData))
        }

        formData === "" ? setFormData(" ") : setFormData("");       
        setCurrentPage(0);
    }

    const handleSearchColumn1 = (e: string): void => {
        setSites([]);
        searchData["siteId"] = e;
        setSearchData(searchData);

        if (siteFilterQuery.length > 0) {
            setSiteIdFilter([]);
            setSiteTypeFilter([]);
            setSiteFilterQuery(JSON.stringify(searchData))
        }

        handleChange();
    }

    const handleSearchColumn2 = (e: string): void => {
        setSites([]);
        searchData["description"] = e;
        setSearchData(searchData);

        if (siteFilterQuery.length > 0) {
            setSiteIdFilter([]);
            setSiteTypeFilter([]);
            setSiteFilterQuery(JSON.stringify(searchData))
        }

        handleChange();
    }

    const handleSearchColumn3 = (e: string): void => {
        setSites([]);
        searchData["customerName"] = e;
        setSearchData(searchData);

        if (siteFilterQuery.length > 0) {
            setSiteIdFilter([]);
            setSiteTypeFilter([]);
            setSiteFilterQuery(JSON.stringify(searchData))
        }

        handleChange();
    }    

    const handleRefreshButton = (): void => {
        setSites([]);
        formData === "" ? setFormData(" ") : setFormData("");
    }

    const delay = async (ms: number) => {
        await new Promise(resolve => setTimeout(resolve, ms));
    }

    let timer: NodeJS.Timeout | null = null;

    const submitSearch = () => {
        formData === "" ? setFormData(" ") : setFormData("");
    }

    const handleChange = () => {
        if (timer) {
            clearTimeout(timer)
        }
        timer = setTimeout(submitSearch, 2000);
        setCurrentPage(0);
    }

    const handleSiteChange = (site: Site) =>
    (e: ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;

        if (checked) {
            setSiteIdFilter(prev => [...(prev ?? []), site.siteId]);
            setSiteTypeFilter(prev => [...(prev ?? []), site.type]);
        } else {
            setSiteIdFilter(prev => (prev ?? []).filter(id => id !== site.siteId));
            setSiteTypeFilter(prev => (prev ?? []).filter(t => t !== site.type));
        }

        setSiteFilterQuery("");
    };

    const handlePaging = async (e: number) => {
        errorMsg.length = 0;

        do {
            await delay(1000);
        } while (isRefreshing);       

        setCurrentPage(e);

        const start = ((e + 1) * 25) - 25;
        const end = ((e + 1) * 25);
        const rangeString = `${start}-${end}`
        setPagingString(rangeString);

        document.querySelector('#refresh')?.classList.remove("d-none");
        setIsRefreshing(true);        

        getSites(keycloak.token || '', rangeString, { search: formData, Online: filterData }, searchData)
            .then((result) => {
                setSites(result.body);
                var totalCount = result.header.split('/')[1];
                setSiteCount(totalCount);
                document.querySelector('#refresh')?.classList.add("d-none");
                setIsRefreshing(false);

                let s: Site = {
                    id: 0,
                    siteId: "",
                    type: "Basic",
                    description: "",
                    customerName: "",
                    paymentReservation: "False",
                    sellOnce: "False",
                    eventLogLevel: "Info",
                    cpsEnabled: "False",
                    cpsAutoDetectPaymentMethod: "False",
                    productAvailabilityViaSessions: "False",
                    maxVmcsPerTerminal: 1,
                    startChargeCardAuthorizationTimeoutInSeconds: 0,
                    startDeliveryTimeoutInSeconds: 60,
                    orderExpirationTimeInMinutes: 0,
                    automaticProductRecoveryCps: "False",
                    automaticProductRecoveryPin: "False",
                    allowSameProductSelection: "False",
                    sendCardTokenInStatusResponse: "False",
                    sendAuthCodeInStatusResponse: "False",
                    deliveryStatusText_WaitingForPayment: "Wachten op betaling",
                    deliveryStatusText_ReservationSucceeded: "Reservering geslaagd",
                    deliveryStatusText_DeliveryStarted: "Levering gestart",
                    deliveryStatusText_DeliveryStopped: "Levering gestopt",
                    deliveryStatusText_PaymentSucceeded: "Betaling geslaagd",
                    deliveryStatusText_PaymentFailed: "Betaling mislukt",
                    deliveryStatusText_DeliveryFailed: "Levering mislukt",
                    deliveryStatusText_Completed: "Levering voltooid",
                    groupBySelection: "True",
                    propertyNames: null//result?.body[0]?.propertyNames
                } as unknown as Site;                

                if (!s?.propertyNames) {
                    getPropertyInfo(keycloak.token || '').then((res: any) => {
                        s.propertyNames = res;
                        s.siteId = res?.siteId?.predefinedValues ? res.siteId.predefinedValues[0] : "";
                        setNewSite(s);
                    }).catch((ex: any) => setNewSite(s));                    
                } else {
                    setNewSite(s);
                }
            })
            .catch((error: ApiError) => {
                try {
                    document.querySelector('.errorMsg')?.classList.remove("d-none");
                    Object.entries<Record<string, any>>(JSON.parse(error.body)["errors"]).forEach((k) => { k[1].forEach((msg: string) => { errorMsg.push(msg); setErrorMsg(errorMsg); }) });
                } catch (ex) {
                    console.log(ex);
                    errorMsg.push("Error handling response");
                    setErrorMsg(errorMsg);
                }

                document.querySelector('#refresh')?.classList.add("d-none");
                setIsRefreshing(false);
            });
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>): boolean => {
        if (event.key === 'Enter') {
            event.preventDefault();
            return false;
        }
        return true;
    }

    const init = () => {
        document.querySelector('.errorMsg')?.classList.add("d-none");
        handlePaging(currentPage ?? 0);
        initSearchFields();
    };

    const initSearchFields = () => {
        Object.keys(searchData).map(x => {
            const search: HTMLInputElement | null = document.querySelector(`.${x}`) as HTMLInputElement;
            if (search) {
                search.value = searchData[x];
            }
        })
    }

    const onResize = () => {
        var e = document.querySelector(".vertical-scrollable") as HTMLElement;
        var h = window.innerHeight;
        e.style.height = (h - 284) + "px";
        e.style.maxHeight = (h - 280) + "px";
        setTableBodyHeight(h - 284);
    };

    function remove(siteIdFilter: string[], id: string) : boolean {
        siteIdFilter.forEach((item, index) => {
            if (item === id) siteIdFilter.splice(index, 1);
        });

        return true;
    }

    useEffect(() => {
        window.addEventListener("resize", onResize);
        onResize();
        if (sites?.length === 0) {
            init();
        }
        clearPendingSelectedId("sites");
        return () => {
            window.removeEventListener("resize", onResize);
        };
    }, []);

    useEffect(() => {
        if (sites.length > 0) return;
        init();
    }, [formData, filterData]);

    return (
        <>
            <Row className="">                  
                <Col className="">
                    <Card className="rounded">                        
                        <Card.Body className="">
                            <Row className="d-none d-md-block">
                                <Col>&nbsp;</Col>
                            </Row>
                            <Row className="d-none d-md-block">
                                <Col>&nbsp;</Col>
                            </Row>
                            <Row className="">
                                <Col sm="12" className="">    
                                    <h4 className="mb-2 qvend-title">{t("sites")}</h4>                                    
                                </Col>
                            </Row>
                            <form className="" onKeyDown={handleKeyDown}>
                                <Row style={{ fontSize: '11px' }}>
                                    <Col md={3} xs={5} style={{ paddingRight: 0 }}>
                                        <div className="bd-example">
                                            <div>
                                                <SearchInput className="form-control search" type="search" placeholder={t("search")} onChange={handleSearch} value={searchData.search ?? ""} />
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={3} xs={1}>
                                        <button type="button" className="btn btn-qvend" style={{ fontSize: '11px', padding: '0.5rem 0.3rem' }}>...</button>
                                    </Col>                                    
                                    <Col className="text-end">
                                        { (newSite && hasRole(newSite, "siteId", keycloak) === true) ? (<button type="button" className="btn btn-qvend" style={{ fontSize: '11px', padding: '0.4rem 0.5rem', marginRight: '6px' }} onClick={() => {
                                            let siteIdValue: string = "";
                                            if (newSite && newSite!.propertyNames && newSite!.propertyNames["siteId"] && newSite!.propertyNames["siteId"]["predefinedValues"]) {
                                                let values = newSite!.propertyNames["siteId"]["predefinedValues"];
                                                if (values.length === 1) {
                                                    siteIdValue = values[0];
                                                }
                                            }
                                            newSite && (newSite.siteId = siteIdValue ? siteIdValue : ""); setData(newSite!); navigate('/dashboard/details/site');
                                        }}>+</button>) : (
                                            <a className="btn btn-qvend" style={{ pointerEvents: 'none', color: 'gray', backgroundColor: 'lightgray', cursor: 'default', fontSize: '11px', padding: '0.5rem 0.5rem', margin: '5px' }}
                                            onClick={(e) => e.preventDefault()}
                                        >+</a>
                                        ) }
                                        <button type="button" className="btn btn-qvend" style={{ fontSize: '11px' }}>Download</button>
                                    </Col>
                                </Row>
                                <Row>
                                <Col md={12} xs={10} style={{ paddingRight: 0 }}>
                                </Col>
                                </Row>
                            </form>
                            <Row>
                                <Col sm="12" xs="12" className="mt-2">
                                    <RefreshPull isRefreshing={isRefreshing} onSetPage={handlePaging} />
                                    <div className="table-responsive-lg" style={{ overflowX: 'hidden' }}>
                                        <div>
                                            <Row className="qvend-table-font" style={{ background: "#2b2e83", color: "#FFFFFF", overflowY: "scroll" }}>
                                                <Col xl={1} lg={1} md={1} sm={1} xs={3} className="qvend-table table-column-first">{t('site.filter')}</Col>
                                                <Col xl={2} lg={2} md={2} sm={2} xs={4} className="qvend-table">{t('site.siteId')}</Col>
                                                <Col xl={4} lg={4} md={4} sm={9} xs={5} className="qvend-table">{t('site.description')}</Col>
                                                <Col xl={5} lg={5} md={5} sm={0} xs={0} className="qvend-table d-none d-md-block">{t('site.customer')}</Col>
                                            </Row>                                            
                                        </div>
                                        <div className="vertical-scrollable" style={{ overscrollBehaviorY: 'contain' }}>
                                            <Row className={("qvend-table-font")} style={{ background: "#cfd5ea", color: "black" }}>
                                                <Col xl={1} lg={1} md={1} sm={1} xs={3} className="qvend-table search-in-row-first">
                                                    <Form.Check checked={ siteFilterQuery.length > 0 } onChange={handleSelectAll} /></Col>
                                                <Col xl={2} lg={2} md={2} sm={2} xs={4} className="qvend-table search-in-row-padding"><SearchInput className="form-control siteId" type="search" onChange={handleSearchColumn1} value={searchData.siteId ?? ""} /></Col>
                                                <Col xl={4} lg={4} md={4} sm={9} xs={5} className="qvend-table search-in-row-padding"><SearchInput className="form-control description" type="search" onChange={handleSearchColumn2} value={searchData.description ?? ""} /></Col>
                                                <Col xl={5} lg={5} md={5} sm={0} xs={0} className="qvend-table search-in-row-padding d-none d-md-block"><SearchInput className="form-control customerName" type="search" onChange={handleSearchColumn3} value={searchData.customerName ?? ""} /></Col>
                                            </Row>
                                            <Row className="d-none errorMsg">
                                                <Col>
                                                    {
                                                        errorMsg.map((item, index) => {
                                                            return <Alert key={index} variant="warning" className="alert" role="alert">
                                                                <span>{item}</span>
                                                            </Alert>;
                                                        })
                                                    }
                                                </Col>
                                            </Row>
                                            {sites.map((site: Site, index) => (

                                                <Row className={"qvend-table-font"} style={{ background: (selectedId === site.id ? "gray" : index % 2 ? "#cfd5ea" : "#e9ebf5"), color: "black", cursor: "pointer" }} onClick={(e) => handleMouseClickOnRow(e, site)}>
                                                        <SiteItem
                                                            key={site.id}
                                                        site={site}
                                                        isChecked={(siteIdFilter !== null && siteIdFilter.indexOf(site.siteId) !== -1) || (siteFilterQuery.length > 0)}
                                                        isDisabled={siteFilterQuery.length > 0}
                                                            handleChange={handleSiteChange(site)}
                                                            rowColor={index % 2 ? "#cfd5ea" : "#e9ebf5"}
                                                        />                                                    
                                                    </Row>
                                                
                                                
                                                ))}
                                        </div>                                        
                                    </div>
                                </Col>                              
                            </Row>
                            <ListPagination
                                articlesCount={siteCount}
                                currentPage={currentPage}
                                onSetPage={handlePaging}
                                onRefresh={handleRefreshButton } />
                        </Card.Body>
                    </Card>
                </Col>                                    
            </Row>
        </>
    )
}

export default Sites

function hasRole(vmc: any, key: string, keycloak: any) {
    if (!(vmc && vmc.propertyNames && vmc.propertyNames[key])) {
        return false;
    }

    const prop = vmc.propertyNames[key];
    const roleNames = prop ? prop.roleNames : null;
    if (roleNames === null || roleNames === undefined) return true;
    let result: boolean = false;
    if (!keycloak) return false;
    roleNames.forEach((role: string) => { result = result || keycloak.hasResourceRole(role); });
    return result;
}
